import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { scooterApi } from "@/lib/api";
import { toast } from "sonner";
import { 
  Droplet, 
  Plus, 
  Calendar, 
  Gauge, 
  User, 
  DollarSign,
  Trash2,
  History
} from "lucide-react";

export function OilChangeHistory({ scooter, onUpdate }) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newEntry, setNewEntry] = useState({
    date: new Date().toISOString().split('T')[0],
    mileage: scooter?.mileage || 0,
    oil_type: "",
    oil_brand: "",
    performed_by: "jedda",
    notes: "",
    cost: 0
  });

  const oilHistory = scooter?.oil_change_history || [];

  const handleAddEntry = async () => {
    if (!newEntry.date || !newEntry.mileage) {
      toast.error("Data și kilometrajul sunt obligatorii");
      return;
    }

    setLoading(true);
    try {
      const updatedHistory = [...oilHistory, newEntry];
      await scooterApi.update(scooter.scooter_id, {
        oil_change_history: updatedHistory
      });
      toast.success("Vidange ajoutée / Schimb de ulei adăugat");
      setIsDialogOpen(false);
      setNewEntry({
        date: new Date().toISOString().split('T')[0],
        mileage: scooter?.mileage || 0,
        oil_type: "",
        oil_brand: "",
        performed_by: "jedda",
        notes: "",
        cost: 0
      });
      onUpdate?.();
    } catch (error) {
      toast.error("Erreur lors de l'ajout / Eroare la adăugare");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEntry = async (index) => {
    if (!window.confirm("Supprimer cette entrée? / Ștergeți această intrare?")) return;
    
    setLoading(true);
    try {
      const updatedHistory = oilHistory.filter((_, i) => i !== index);
      await scooterApi.update(scooter.scooter_id, {
        oil_change_history: updatedHistory
      });
      toast.success("Entrée supprimée / Intrare ștearsă");
      onUpdate?.();
    } catch (error) {
      toast.error("Erreur lors de la suppression / Eroare la ștergere");
    } finally {
      setLoading(false);
    }
  };

  const getPerformedByLabel = (value) => {
    const labels = {
      jedda: "Scutersh",
      autre: "Autre / Altul",
      client: "Client"
    };
    return labels[value] || value;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("ro-RO", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  // Calculate next oil change recommendation (every 3000km or 6 months)
  const getNextOilChange = () => {
    if (oilHistory.length === 0) return null;
    
    const lastChange = [...oilHistory].sort((a, b) => 
      new Date(b.date) - new Date(a.date)
    )[0];
    
    const nextMileage = (lastChange.mileage || 0) + 3000;
    const lastDate = new Date(lastChange.date);
    const nextDate = new Date(lastDate);
    nextDate.setMonth(nextDate.getMonth() + 6);
    
    return {
      nextMileage,
      nextDate: nextDate.toISOString().split('T')[0],
      lastChange
    };
  };

  const nextChange = getNextOilChange();

  return (
    <Card className="border-border" data-testid="oil-change-history">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Droplet className="w-5 h-5 text-amber-500" />
            Historique Vidanges / Istoric Schimburi Ulei
          </CardTitle>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="btn-add-oil-change">
                <Plus className="w-4 h-4 mr-2" />
                Ajouter / Adaugă
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Droplet className="w-5 h-5 text-amber-500" />
                  Nouveau Schimb de Ulei
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="oil-date">Date / Data *</Label>
                    <Input
                      id="oil-date"
                      type="date"
                      value={newEntry.date}
                      onChange={(e) => setNewEntry({...newEntry, date: e.target.value})}
                      data-testid="input-oil-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="oil-mileage">Kilometraj *</Label>
                    <Input
                      id="oil-mileage"
                      type="number"
                      value={newEntry.mileage}
                      onChange={(e) => setNewEntry({...newEntry, mileage: parseInt(e.target.value) || 0})}
                      data-testid="input-oil-mileage"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="oil-type">Type d'huile / Tip ulei</Label>
                    <Input
                      id="oil-type"
                      placeholder="ex: 10W-40"
                      value={newEntry.oil_type}
                      onChange={(e) => setNewEntry({...newEntry, oil_type: e.target.value})}
                      data-testid="input-oil-type"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="oil-brand">Marque / Marcă</Label>
                    <Input
                      id="oil-brand"
                      placeholder="ex: Motul, Castrol"
                      value={newEntry.oil_brand}
                      onChange={(e) => setNewEntry({...newEntry, oil_brand: e.target.value})}
                      data-testid="input-oil-brand"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="performed-by">Effectué par / Efectuat de</Label>
                  <select
                    id="performed-by"
                    value={newEntry.performed_by}
                    onChange={(e) => setNewEntry({...newEntry, performed_by: e.target.value})}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    data-testid="select-performed-by"
                  >
                    <option value="jedda">Scutersh</option>
                    <option value="autre">Autre / Altul</option>
                    <option value="client">Client</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="oil-cost">Coût / Cost (EUR)</Label>
                  <Input
                    id="oil-cost"
                    type="number"
                    value={newEntry.cost}
                    onChange={(e) => setNewEntry({...newEntry, cost: parseFloat(e.target.value) || 0})}
                    data-testid="input-oil-cost"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="oil-notes">Notes / Note</Label>
                  <Textarea
                    id="oil-notes"
                    placeholder="Observații..."
                    value={newEntry.notes}
                    onChange={(e) => setNewEntry({...newEntry, notes: e.target.value})}
                    rows={2}
                    data-testid="input-oil-notes"
                  />
                </div>

                <Button 
                  className="w-full" 
                  onClick={handleAddEntry}
                  disabled={loading}
                  data-testid="btn-save-oil-change"
                >
                  {loading ? "Se salvează..." : "Enregistrer / Salvează"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Next Oil Change Recommendation */}
        {nextChange && (
          <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/30">
            <p className="text-sm font-medium text-amber-600 mb-1">
              📅 Prochaine vidange recommandée / Următorul schimb recomandat:
            </p>
            <p className="text-sm text-muted-foreground">
              La <span className="font-mono font-bold">{nextChange.nextMileage.toLocaleString()} km</span> ou avant le <span className="font-bold">{formatDate(nextChange.nextDate)}</span>
            </p>
            {scooter?.mileage >= nextChange.nextMileage && (
              <Badge className="mt-2 bg-red-500 text-white">⚠️ Vidange en retard!</Badge>
            )}
          </div>
        )}

        {/* History List */}
        {oilHistory.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <History className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>Aucun historique de vidange</p>
            <p className="text-sm">Pas de istoric schimburi ulei</p>
          </div>
        ) : (
          <div className="space-y-3">
            {[...oilHistory].reverse().map((entry, idx) => (
              <div 
                key={idx} 
                className="p-3 bg-secondary/50 rounded-lg group"
                data-testid={`oil-entry-${idx}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Badge variant="outline" className="font-mono">
                        <Calendar className="w-3 h-3 mr-1" />
                        {formatDate(entry.date)}
                      </Badge>
                      <Badge variant="outline" className="font-mono">
                        <Gauge className="w-3 h-3 mr-1" />
                        {entry.mileage?.toLocaleString()} km
                      </Badge>
                    </div>
                    <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
                      {entry.oil_type && (
                        <span className="flex items-center gap-1">
                          <Droplet className="w-3 h-3" />
                          {entry.oil_type}
                        </span>
                      )}
                      {entry.oil_brand && (
                        <span>• {entry.oil_brand}</span>
                      )}
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {getPerformedByLabel(entry.performed_by)}
                      </span>
                      {entry.cost > 0 && (
                        <span className="flex items-center gap-1">
                          <DollarSign className="w-3 h-3" />
                          {entry.cost} EUR
                        </span>
                      )}
                    </div>
                    {entry.notes && (
                      <p className="text-xs text-muted-foreground mt-2 italic">
                        {entry.notes}
                      </p>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => handleDeleteEntry(oilHistory.length - 1 - idx)}
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default OilChangeHistory;
